/**
 * AgentServicePanel
 * Windows Service installation and lifecycle management panel.
 */

import {
  Box,
  Typography,
  Chip,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  InstallDesktop as InstallIcon,
  Delete as UninstallIcon,
  RestartAlt as RestartIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  AdminPanelSettings as ElevationIcon,
} from '@mui/icons-material';
import { useEffect } from 'react';
import { useSendCommand } from '@/hooks/useAgents';
import { useAgentsStore } from '@/store/agentsStore';
import type { Agent } from '@/types/agent';

interface AgentServicePanelProps {
  agent: Agent;
}

const StatusDot = ({ ok, label }: { ok: boolean; label: string }) => (
  <Box display="flex" alignItems="center" gap={0.5}>
    {ok ? (
      <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
    ) : (
      <CancelIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
    )}
    <Typography variant="caption" color={ok ? 'text.primary' : 'text.disabled'}>
      {label}
    </Typography>
  </Box>
);

export const AgentServicePanel: React.FC<AgentServicePanelProps> = ({ agent }) => {
  const svc = agent.serviceStatus;
  const sendCommand = useSendCommand(agent.agentId);
  const { getServiceEvent, clearServiceEvent } = useAgentsStore();
  const serviceEvent = getServiceEvent(agent.agentId);

  // Auto-clear terminal events after 8s
  useEffect(() => {
    if (!serviceEvent) return undefined;
    const terminal = ['installed', 'uninstalled', 'restarted', 'started', 'stopped'];
    if (terminal.includes(serviceEvent.event)) {
      const t = setTimeout(() => clearServiceEvent(agent.agentId), 8000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [serviceEvent, agent.agentId, clearServiceEvent]);

  // Only show for connected Windows agents
  if (!agent.connected || agent.os?.toLowerCase() !== 'windows') {
    return null;
  }

  const isOperationPending =
    serviceEvent?.event === 'awaiting_elevation' ||
    (sendCommand.isPending &&
      [
        'service.install',
        'service.uninstall',
        'service.restart',
        'service.start',
        'service.stop',
      ].includes(sendCommand.variables?.command ?? ''));

  const handleInstall = () => {
    sendCommand.mutate({
      command: 'service.install',
      args: { backendUrl: '' }, // agent uses its current backendUrl
    });
  };

  const handleUninstall = () => {
    if (!confirm('¿Desinstalar el servicio de Windows? El agente seguirá corriendo como portable.'))
      return;
    sendCommand.mutate({ command: 'service.uninstall' });
  };

  const handleRestart = () => {
    sendCommand.mutate({ command: 'service.restart' });
  };

  const handleStart = () => {
    sendCommand.mutate({ command: 'service.start' });
  };

  const handleStop = () => {
    if (!confirm('¿Detener el servicio? El agente perderá la conexión hasta el próximo arranque.'))
      return;
    sendCommand.mutate({ command: 'service.stop' });
  };

  const isInstalled = svc?.serviceInstalled ?? false;
  const isRunning = svc?.serviceRunning ?? false;
  const modeLabel = svc?.mode === 'service' ? 'Servicio' : 'Portable';
  const modeColor = svc?.mode === 'service' ? 'primary' : 'default';

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Instalación como Servicio
      </Typography>

      {/* Mode + status dots */}
      <Box mb={1.5}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Chip label={modeLabel} size="small" color={modeColor} variant="outlined" />
          {svc?.elevationRequired && (
            <Tooltip title="Se requieren permisos de administrador para operaciones de servicio">
              <ElevationIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            </Tooltip>
          )}
        </Box>
        {svc ? (
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <StatusDot ok={isInstalled} label="Instalado" />
            <StatusDot ok={isRunning} label="Corriendo" />
            <StatusDot ok={svc.startupEnabled} label="Arranque automático" />
          </Stack>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Estado no disponible
          </Typography>
        )}
      </Box>

      {/* UAC elevation warning */}
      {svc?.elevationRequired && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5, py: 0.5 }}
          icon={<ElevationIcon fontSize="small" />}
        >
          <Typography variant="caption">
            Requiere UAC. El agente mostrará el diálogo de elevación en la pantalla remota.
          </Typography>
        </Alert>
      )}

      {/* Operation feedback */}
      {serviceEvent && (
        <Alert
          severity={
            serviceEvent.event.includes('failed')
              ? 'error'
              : serviceEvent.event === 'awaiting_elevation'
                ? 'warning'
                : 'success'
          }
          sx={{ mb: 1.5, py: 0.5 }}
          onClose={() => clearServiceEvent(agent.agentId)}
        >
          <Typography variant="caption">
            {serviceEvent.event === 'awaiting_elevation' &&
              'Esperando aprobación UAC en pantalla remota...'}
            {serviceEvent.event === 'installed' && 'Servicio instalado e iniciado correctamente.'}
            {serviceEvent.event === 'install_failed' &&
              `Error al instalar: ${serviceEvent.error ?? 'desconocido'}`}
            {serviceEvent.event === 'uninstalled' && 'Servicio desinstalado correctamente.'}
            {serviceEvent.event === 'uninstall_failed' &&
              `Error al desinstalar: ${serviceEvent.error ?? 'desconocido'}`}
            {serviceEvent.event === 'restarted' && 'Servicio reiniciado correctamente.'}
            {serviceEvent.event === 'restart_failed' &&
              `Error al reiniciar: ${serviceEvent.error ?? 'desconocido'}`}
            {serviceEvent.event === 'started' && 'Servicio iniciado correctamente.'}
            {serviceEvent.event === 'start_failed' &&
              `Error al iniciar: ${serviceEvent.error ?? 'desconocido'}`}
            {serviceEvent.event === 'stopped' && 'Servicio detenido.'}
            {serviceEvent.event === 'stop_failed' &&
              `Error al detener: ${serviceEvent.error ?? 'desconocido'}`}
          </Typography>
        </Alert>
      )}

      {/* Action buttons */}
      <Stack spacing={1}>
        {!isInstalled ? (
          <Button
            variant="contained"
            size="small"
            startIcon={
              isOperationPending ? <CircularProgress size={14} color="inherit" /> : <InstallIcon />
            }
            onClick={handleInstall}
            disabled={isOperationPending}
            fullWidth
          >
            Instalar como Servicio
          </Button>
        ) : (
          <>
            {isRunning ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  isOperationPending ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <RestartIcon />
                  )
                }
                onClick={handleRestart}
                disabled={isOperationPending}
                fullWidth
              >
                Reiniciar Servicio
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                color="success"
                startIcon={
                  isOperationPending ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <StartIcon />
                  )
                }
                onClick={handleStart}
                disabled={isOperationPending}
                fullWidth
              >
                Iniciar Servicio
              </Button>
            )}
            {isRunning && (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                startIcon={
                  isOperationPending ? <CircularProgress size={14} color="inherit" /> : <StopIcon />
                }
                onClick={handleStop}
                disabled={isOperationPending}
                fullWidth
              >
                Detener Servicio
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={
                isOperationPending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <UninstallIcon />
                )
              }
              onClick={handleUninstall}
              disabled={isOperationPending}
              fullWidth
            >
              Desinstalar Servicio
            </Button>
          </>
        )}
      </Stack>

      {/* Paths info */}
      {svc?.configPath && isInstalled && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Config: {svc.configPath}
        </Typography>
      )}
    </>
  );
};
